/**
 * Chart Tools Sidebar — TradingView-style left toolbar
 *
 * Vertical toolbar with tool groups, hover submenus, color picker,
 * zoom controls, magnet toggle, and drawing management actions.
 *
 * Layout reference: TradingView / MT4 / AmiBroker left sidebar
 */

import React, { useState, useRef, useCallback } from "react";
import { DrawingTool, DRAWING_COLORS } from "../../types/drawing.types";
import "./ChartToolsSidebar.scss";

// ── Props ─────────────────────────────────────────────────────────
interface ChartToolsSidebarProps {
  activeTool: DrawingTool;
  activeColor: string;
  magnetEnabled: boolean;
  drawingsVisible: boolean;
  drawingCount: number;
  onToolChange: (tool: DrawingTool) => void;
  onColorChange: (color: string) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onMagnetToggle: () => void;
  onToggleVisibility: () => void;
  onClearAll: () => void;
  onUndo: () => void;
  colorTheme: string;
}

// ── Tool group definitions ────────────────────────────────────────
interface ToolDef {
  key: DrawingTool;
  label: string;
}

interface ToolGroupDef {
  id: string;
  tools: ToolDef[];
}

const TOOL_GROUPS: ToolGroupDef[] = [
  {
    id: "pointer",
    tools: [{ key: "cursor", label: "Cursor" }],
  },
  {
    id: "lines",
    tools: [
      { key: "trendline", label: "Trend Line" },
      { key: "ray", label: "Ray" },
      { key: "extended-line", label: "Extended Line" },
      { key: "horizontal-ray", label: "Horizontal Ray" },
    ],
  },
  {
    id: "horizontal",
    tools: [{ key: "horizontal", label: "Horizontal Line" }],
  },
  {
    id: "vertical",
    tools: [{ key: "vertical", label: "Vertical Line" }],
  },
  {
    id: "channel",
    tools: [{ key: "parallel-channel", label: "Parallel Channel" }],
  },
  {
    id: "fibonacci",
    tools: [
      { key: "fibonacci", label: "Fib Retracement" },
      { key: "fib-extension", label: "Fib Extension" },
      { key: "fib-fan", label: "Fib Fan" },
      { key: "fib-timezone", label: "Fib Time Zones" },
      { key: "fib-arc", label: "Fib Arc" },
    ],
  },
  {
    id: "gann",
    tools: [{ key: "gann-fan", label: "Gann Fan" }],
  },
  {
    id: "pitchfork",
    tools: [{ key: "pitchfork", label: "Pitchfork" }],
  },
  {
    id: "shapes",
    tools: [
      { key: "rectangle", label: "Rectangle" },
      { key: "ellipse", label: "Ellipse" },
      { key: "triangle", label: "Triangle" },
    ],
  },
  {
    id: "annotations",
    tools: [
      { key: "text", label: "Text" },
      { key: "arrow", label: "Arrow" },
      { key: "price-label", label: "Price Label" },
    ],
  },
  {
    id: "measure",
    tools: [{ key: "measure", label: "Measure" }],
  },
];

// ── SVG icon stroke props (feather-style) ─────────────────────────
const S: React.SVGAttributes<SVGElement> = {
  stroke: "currentColor",
  fill: "none",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

// ── Tool icon renderer ────────────────────────────────────────────
function renderToolIcon(tool: DrawingTool, size = 24): React.ReactNode {
  const vb = "0 0 24 24";
  switch (tool) {
    case "cursor":
      return (
        <svg viewBox={vb} width={size} height={size}>
          {/* Top arm */}
          <line x1="12" y1="2" x2="12" y2="9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          {/* Bottom arm */}
          <line x1="12" y1="15" x2="12" y2="22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          {/* Left arm */}
          <line x1="2" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          {/* Right arm */}
          <line x1="15" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          {/* Center dot */}
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
        </svg>
      );
    case "trendline":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <circle cx="4" cy="20" r="2" fill="currentColor" />
          <circle cx="20" cy="4" r="2" fill="currentColor" />
        </svg>
      );
    case "ray":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="18" x2="20" y2="6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <circle cx="4" cy="18" r="2" fill="currentColor" />
          <polyline points="16,4 20,6 17,10" {...S} />
        </svg>
      );
    case "extended-line":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="1" y1="22" x2="23" y2="2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeDasharray="2 3" />
          <line x1="6" y1="17" x2="18" y2="7" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <circle cx="8" cy="16" r="2" fill="currentColor" />
          <circle cx="16" cy="8" r="2" fill="currentColor" />
        </svg>
      );
    case "horizontal-ray":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="3" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <circle cx="3" cy="12" r="2" fill="currentColor" />
          <polyline points="19,9 22,12 19,15" {...S} />
        </svg>
      );
    case "horizontal":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <line x1="2" y1="9" x2="2" y2="15" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="22" y1="9" x2="22" y2="15" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
        </svg>
      );
    case "vertical":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="12" y1="2" x2="12" y2="22" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <line x1="9" y1="2" x2="15" y2="2" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="9" y1="22" x2="15" y2="22" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
        </svg>
      );
    case "parallel-channel":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="2" y1="14" x2="22" y2="6" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="2" y1="18" x2="22" y2="10" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="2" y1="14" x2="2" y2="18" stroke="currentColor" strokeWidth={1} strokeDasharray="2 2" />
          <line x1="22" y1="6" x2="22" y2="10" stroke="currentColor" strokeWidth={1} strokeDasharray="2 2" />
        </svg>
      );
    case "fibonacci":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="4" x2="20" y2="4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="4" y1="9.5" x2="20" y2="9.5" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" />
          <line x1="4" y1="14.5" x2="20" y2="14.5" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" />
          <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <text x="2" y="7" fill="currentColor" fontSize="5" fontFamily="monospace">0</text>
          <text x="1" y="22.5" fill="currentColor" fontSize="5" fontFamily="monospace">1</text>
        </svg>
      );
    case "fib-extension":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="4" x2="20" y2="4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="4" y1="10" x2="20" y2="10" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" />
          <line x1="4" y1="16" x2="20" y2="16" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" />
          <line x1="4" y1="20" x2="20" y2="20" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <polyline points="6,20 11,10 15,16" stroke="currentColor" strokeWidth={1.5} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "rectangle":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <rect x="3" y="5" width="18" height="14" rx="1" stroke="currentColor" strokeWidth={1.5} fill="none" />
        </svg>
      );
    case "ellipse":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <ellipse cx="12" cy="12" rx="9" ry="7" stroke="currentColor" strokeWidth={1.5} fill="none" />
        </svg>
      );
    case "triangle":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <polygon
            points="12,3 3,21 21,21"
            stroke="currentColor" strokeWidth={1.5} fill="none"
            strokeLinejoin="round"
          />
        </svg>
      );
    case "text":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="6" y1="5" x2="18" y2="5" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <line x1="12" y1="5" x2="12" y2="19" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <line x1="9" y1="19" x2="15" y2="19" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
        </svg>
      );
    case "arrow":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="5" y1="19" x2="19" y2="5" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
          <polyline points="12,5 19,5 19,12" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "price-label":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <rect x="3" y="7" width="18" height="10" rx="2" stroke="currentColor" strokeWidth={1.5} fill="none" />
          <text x="12" y="15" fill="currentColor" fontSize="9" textAnchor="middle" fontFamily="monospace" fontWeight="bold">₹</text>
        </svg>
      );
    case "measure":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <rect x="3" y="5" width="18" height="14" rx="1" stroke="currentColor" strokeWidth={1.5} fill="none" />
          <line x1="7" y1="5" x2="7" y2="19" stroke="currentColor" strokeWidth={0.8} />
          <line x1="11" y1="5" x2="11" y2="19" stroke="currentColor" strokeWidth={0.5} strokeDasharray="2 2" />
          <line x1="15" y1="5" x2="15" y2="19" stroke="currentColor" strokeWidth={0.5} strokeDasharray="2 2" />
          <line x1="19" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth={0.8} />
          <text x="12" y="13.5" fill="currentColor" fontSize="6" textAnchor="middle" fontFamily="monospace">%</text>
        </svg>
      );
    case "fib-fan":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="4" y1="20" x2="20" y2="8" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <line x1="4" y1="20" x2="20" y2="12" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <line x1="4" y1="20" x2="20" y2="16" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <circle cx="4" cy="20" r="2" fill="currentColor" />
        </svg>
      );
    case "fib-timezone":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="4" x2="4" y2="20" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="7" y1="4" x2="7" y2="20" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <line x1="11" y1="4" x2="11" y2="20" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <line x1="16" y1="4" x2="16" y2="20" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <text x="5" y="8" fill="currentColor" fontSize="5" fontFamily="monospace">0</text>
          <text x="8" y="8" fill="currentColor" fontSize="5" fontFamily="monospace">1</text>
          <text x="12" y="8" fill="currentColor" fontSize="5" fontFamily="monospace">2</text>
        </svg>
      );
    case "fib-arc":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth={1.5} strokeDasharray="3 2" opacity="0.5" />
          <circle cx="12" cy="12" r="6" fill="none" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.6" />
          <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
        </svg>
      );
    case "gann-fan":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="20" x2="20" y2="4" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="4" y1="20" x2="20" y2="6" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.6" />
          <line x1="4" y1="20" x2="20" y2="10" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.6" />
          <line x1="4" y1="20" x2="20" y2="14" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.6" />
          <line x1="4" y1="20" x2="20" y2="18" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.6" />
          <circle cx="4" cy="20" r="2" fill="currentColor" />
        </svg>
      );
    case "pitchfork":
      return (
        <svg viewBox={vb} width={size} height={size}>
          <line x1="4" y1="18" x2="20" y2="6" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          <line x1="4" y1="14" x2="20" y2="4" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <line x1="4" y1="22" x2="20" y2="10" stroke="currentColor" strokeWidth={1} strokeDasharray="3 2" opacity="0.7" />
          <circle cx="4" cy="18" r="2" fill="currentColor" />
          <circle cx="4" cy="14" r="1.5" fill="currentColor" opacity="0.7" />
          <circle cx="4" cy="22" r="1.5" fill="currentColor" opacity="0.7" />
        </svg>
      );
    default:
      return null;
  }
}

// ── Component ─────────────────────────────────────────────────────
const ChartToolsSidebar: React.FC<ChartToolsSidebarProps> = ({
  activeTool,
  activeColor,
  magnetEnabled,
  drawingsVisible,
  drawingCount,
  onToolChange,
  onColorChange,
  onZoomIn,
  onZoomOut,
  onMagnetToggle,
  onToggleVisibility,
  onClearAll,
  onUndo,
  colorTheme,
}) => {
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [tapOpenGroup, setTapOpenGroup] = useState<string | null>(null);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track last-selected tool per group (TradingView behavior: icon reflects last pick)
  const [selectedPerGroup, setSelectedPerGroup] = useState<Record<string, DrawingTool>>(() => {
    const defaults: Record<string, DrawingTool> = {};
    TOOL_GROUPS.forEach(g => { defaults[g.id] = g.tools[0].key; });
    return defaults;
  });

  // Find which group the active tool belongs to
  const activeGroupId = TOOL_GROUPS.find(g => g.tools.some(t => t.key === activeTool))?.id;

  const handleToolSelect = useCallback((groupId: string, tool: DrawingTool) => {
    setSelectedPerGroup(prev => ({ ...prev, [groupId]: tool }));
    onToolChange(tool);
    setHoveredGroup(null);
    setTapOpenGroup(null);
  }, [onToolChange]);

  const handleGroupClick = useCallback((groupId: string, hasSubmenu: boolean) => {
    // If submenu is already open for this group (tap toggle), close it and select the tool
    if (tapOpenGroup === groupId) {
      setTapOpenGroup(null);
      const selectedTool = selectedPerGroup[groupId];
      if (selectedTool === activeTool && selectedTool !== "cursor") {
        onToolChange("cursor");
      } else {
        onToolChange(selectedTool);
      }
      return;
    }
    // If group has a submenu and is not the currently open one, open the submenu on tap
    if (hasSubmenu) {
      setTapOpenGroup(groupId);
      return;
    }
    const selectedTool = selectedPerGroup[groupId];
    if (selectedTool === activeTool && selectedTool !== "cursor") {
      onToolChange("cursor"); // toggle off
    } else {
      onToolChange(selectedTool);
    }
  }, [activeTool, selectedPerGroup, onToolChange, tapOpenGroup]);

  const handleGroupEnter = useCallback((groupId: string) => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    const group = TOOL_GROUPS.find(g => g.id === groupId);
    if (group && group.tools.length > 1) {
      setHoveredGroup(groupId);
    }
  }, []);

  const handleGroupLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredGroup(null), 250);
  }, []);

  const handleSubmenuEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
  }, []);

  // Color picker visibility — show when a drawing tool is active
  const showColorPicker = activeTool !== "cursor";

  return (
    <div className={`chart-tools-sidebar ${colorTheme}`}>
      {/* ── Tool Groups ───────────────────────────────────────── */}
      <div className="sidebar-tools">
        {TOOL_GROUPS.map((group) => {
          const displayTool = selectedPerGroup[group.id] || group.tools[0].key;
          const isActive = activeGroupId === group.id && activeTool !== "cursor";
          const isPointerActive = group.id === "pointer" && activeTool === "cursor";
          const hasSubmenu = group.tools.length > 1;

          return (
            <div
              key={group.id}
              className={`tool-group ${isActive || isPointerActive ? "active" : ""}`}
              onMouseEnter={() => handleGroupEnter(group.id)}
              onMouseLeave={handleGroupLeave}
            >
              <button
                className="tool-group-btn"
                onClick={() => handleGroupClick(group.id, hasSubmenu)}
                title={group.tools.find(t => t.key === displayTool)?.label || ""}
              >
                {renderToolIcon(displayTool)}
                {hasSubmenu && <span className="submenu-arrow">◂</span>}
              </button>

              {/* Hover submenu (mouse) or tap-open submenu (touch) */}
              {(hoveredGroup === group.id || tapOpenGroup === group.id) && hasSubmenu && (
                <div
                  className="tool-submenu"
                  onMouseEnter={handleSubmenuEnter}
                  onMouseLeave={handleGroupLeave}
                >
                  {group.tools.map(t => (
                    <button
                      key={t.key}
                      className={`submenu-item ${activeTool === t.key ? "active" : ""}`}
                      onClick={() => handleToolSelect(group.id, t.key)}
                    >
                      <span className="submenu-icon">{renderToolIcon(t.key, 18)}</span>
                      <span className="submenu-label">{t.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="sidebar-divider" />

      {/* ── Color Picker ──────────────────────────────────────── */}
      {showColorPicker && (
        <>
          <div className="sidebar-colors">
            {DRAWING_COLORS.map(color => (
              <button
                key={color}
                className={`sidebar-color-btn ${activeColor === color ? "selected" : ""}`}
                style={{ backgroundColor: color }}
                onClick={() => onColorChange(color)}
                title={color}
              />
            ))}
          </div>
          <div className="sidebar-divider" />
        </>
      )}

      {/* ── Visibility Toggle ────────────────────────────────── */}
      <div className="sidebar-actions">
        <button
          className={`sidebar-action-btn ${!drawingsVisible ? "toggled-off" : ""}`}
          onClick={onToggleVisibility}
          title={drawingsVisible ? "Hide All Drawings" : "Show All Drawings"}
        >
          {drawingsVisible ? (
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" {...S} />
              <circle cx="12" cy="12" r="3" {...S} />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="22" height="22">
              <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" {...S} />
              <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" {...S} />
              <line x1="1" y1="1" x2="23" y2="23" {...S} />
            </svg>
          )}
        </button>
      </div>

      {/* ── Drawing management ─────────────────────────────────── */}
      {drawingCount > 0 && (
        <>
          <div className="sidebar-divider" />
          <div className="sidebar-actions">
            <button className="sidebar-action-btn" onClick={onUndo} title="Undo Last Drawing">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <polyline points="1,4 1,10 7,10" {...S} />
                <path d="M3.51 15a9 9 0 102.13-9.36L1 10" {...S} />
              </svg>
            </button>
            <button className="sidebar-action-btn danger" onClick={onClearAll} title="Clear All Drawings">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <polyline points="3,6 5,6 21,6" {...S} />
                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" {...S} />
              </svg>
            </button>
            <span className="sidebar-badge">{drawingCount}</span>
          </div>
        </>
      )}
    </div>
  );
};

export default ChartToolsSidebar;
